import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { DataSourceTypes } from "@common/types/data-source-types";
import { MachineNameAndSourceId } from "@common/types/server-list-types";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import apiUseCases from "@src/cloud-connect/api";
import { parseAxiosApiError } from "@src/cloud-connect/utils/parse-axios-api-error";
import store from "@src/reducers/store";
import { patchTicketSourceIdUseCase } from "@src/cloud-connect/support/use-cases/patch-ticket-source-id-use-case";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

const getSourcesUseCase = async (): Promise<{
  success: boolean;
  data?: DataSourceTypes[];
  error?: string;
}> => {
  try {
    const namespace = store.getState().yourSlice.authContext.customerNamespace;
    const response = await apiUseCases.getAllSourcesList(namespace);

    return { success: true, data: Object.values(response.data_sources) };
  } catch (error) {
    return {
      success: false,
      error: parseAxiosApiError(error, "Failed to fetch sources list").message,
    };
  }
};

interface UseTicketSourceSelectionResult {
  sourceOptions: MachineNameAndSourceId[];
  selectedSource: MachineNameAndSourceId | null;
  isLoading: boolean;
  isSelectionFixed: boolean;
  isPatchingSource: boolean;
  isSSEUnavailable: boolean;
  onSelectionChange: (source: MachineNameAndSourceId | null) => void;
  confirmSourceSelection: () => Promise<void>;
}

export const useTicketSourceSelection = (
  ticketId: string | undefined,
  ticketSourceId: string | null | undefined,
  callbackForRefresh: () => void,
): UseTicketSourceSelectionResult => {
  const [sources, setSources] = useState<DataSourceTypes[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPatchingSource, setIsPatchingSource] = useState(false);
  const [pendingSource, setPendingSource] =
    useState<MachineNameAndSourceId | null>(null);
  const patchRequestGenerationRef = useRef(0);
  const sourceRequestGenerationRef = useRef(0);
  const patchInProgressRef = useRef(false);
  const activeTicketIdRef = useRef(ticketId);

  const isSourceAssigned = Boolean(ticketSourceId);
  const isSelectionFixed = isSourceAssigned;
  const isSSEUnavailable = useSSEUnavailable("service-support");
  const isSSEUnavailableRef = useRef(isSSEUnavailable);

  useLayoutEffect(() => {
    activeTicketIdRef.current = ticketId;
    patchRequestGenerationRef.current += 1;
    sourceRequestGenerationRef.current += 1;

    setPendingSource(null);
    patchInProgressRef.current = false;
    setIsPatchingSource(false);

    return () => {
      patchRequestGenerationRef.current += 1;
      patchInProgressRef.current = false;
      if (activeTicketIdRef.current === ticketId) {
        activeTicketIdRef.current = undefined;
      }
    };
  }, [ticketId]);

  useLayoutEffect(() => {
    isSSEUnavailableRef.current = isSSEUnavailable;
  }, [isSSEUnavailable]);

  const loadSources = useCallback(async () => {
    const requestGeneration = ++sourceRequestGenerationRef.current;
    setIsLoading(true);
    const result = await getSourcesUseCase();

    if (requestGeneration !== sourceRequestGenerationRef.current) {
      return;
    }

    if (!result.success || !result.data) {
      errorSnackbar(result.error);
      setIsLoading(false);
      return;
    }
    setSources(result.data);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!ticketId) {
      return;
    }
    void loadSources();
    return () => {
      sourceRequestGenerationRef.current += 1;
    };
  }, [ticketId, loadSources]);

  const sourceToOption = (source: DataSourceTypes): MachineNameAndSourceId => ({
    name: source.name,
    source_id: source.source_id,
  });

  const selectedSource: MachineNameAndSourceId | null = ticketSourceId
    ? {
        name:
          sources.find((source) => source.source_id === ticketSourceId)?.name ||
          ticketSourceId,
        source_id: ticketSourceId,
      }
    : pendingSource;

  const onSelectionChange = (source: MachineNameAndSourceId | null) => {
    if (
      activeTicketIdRef.current !== ticketId ||
      isSelectionFixed ||
      isPatchingSource ||
      patchInProgressRef.current ||
      isSSEUnavailableRef.current
    ) {
      return;
    }
    setPendingSource(source);
  };

  const confirmSourceSelection = async () => {
    if (
      !ticketId ||
      activeTicketIdRef.current !== ticketId ||
      isSelectionFixed ||
      isPatchingSource ||
      patchInProgressRef.current ||
      isSSEUnavailableRef.current ||
      !pendingSource
    ) {
      return;
    }

    const sourceId = pendingSource.source_id;
    const requestTicketId = ticketId;
    const requestGeneration = patchRequestGenerationRef.current;
    patchInProgressRef.current = true;
    setIsPatchingSource(true);
    const result = await patchTicketSourceIdUseCase(sourceId, requestTicketId);

    if (
      requestGeneration !== patchRequestGenerationRef.current ||
      activeTicketIdRef.current !== requestTicketId
    ) {
      return;
    }

    setIsPatchingSource(false);

    if (!result.success) {
      patchInProgressRef.current = false;
      errorSnackbar(result.error);
      return;
    }
    callbackForRefresh();
  };

  return {
    sourceOptions: sources.map(sourceToOption),
    selectedSource,
    isLoading,
    isSelectionFixed,
    isPatchingSource,
    isSSEUnavailable,
    onSelectionChange,
    confirmSourceSelection,
  };
};
